// Copyright 2026, Auxilio Technologies LLC

/**
 * Role-Based Access Control (RBAC) Configuration
 *
 * Roles:
 * - superuser: Full system access including user administration and system settings
 * - admin: Full operational access, can manage users and view everything
 * - transport_manager: Operations focus - shipments, routes, suppliers, alerts
 * - site_manager: Site-specific operations and visibility
 * - planner: Route planning and forecasting
 * - analyst: Analytics and reporting focus
 * - viewer: Read-only access to assigned areas
 */

// Role identifiers - must match backend Roles class
export const Roles = {
  SUPERUSER: 'superuser',
  ADMIN: 'admin',
  TRANSPORT_MANAGER: 'transport_manager',
  SITE_MANAGER: 'site_manager',
  PLANNER: 'planner',
  ANALYST: 'analyst',
  VIEWER: 'viewer',
} as const;

export type RoleType = typeof Roles[keyof typeof Roles];

// Permission identifiers
export const Permissions = {
  // Dashboard - everyone gets this
  DASHBOARD_READ: 'dashboard:read',

  // Control Tower
  CONTROL_TOWER_READ: 'control_tower:read',
  CONTROL_TOWER_WRITE: 'control_tower:write',

  // Shipments
  SHIPMENTS_READ: 'shipments:read',
  SHIPMENTS_WRITE: 'shipments:write',
  SHIPMENTS_DELETE: 'shipments:delete',

  // Routes
  ROUTES_READ: 'routes:read',
  ROUTES_WRITE: 'routes:write',
  ROUTES_DELETE: 'routes:delete',

  // Alerts
  ALERTS_READ: 'alerts:read',
  ALERTS_WRITE: 'alerts:write',
  ALERTS_CONFIG: 'alerts:config',

  // Forecasting
  FORECASTING_READ: 'forecasting:read',
  FORECASTING_WRITE: 'forecasting:write',

  // Suppliers
  SUPPLIERS_READ: 'suppliers:read',
  SUPPLIERS_WRITE: 'suppliers:write',

  // Analytics
  ANALYTICS_READ: 'analytics:read',
  ANALYTICS_EXPORT: 'analytics:export',

  // Sites
  SITES_READ: 'sites:read',
  SITES_WRITE: 'sites:write',

  // Settings
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',

  // User Administration
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  USERS_DELETE: 'users:delete',
  USERS_INVITE: 'users:invite',

  // System Administration
  SYSTEM_CONFIG: 'system:config',
} as const;

export type PermissionType = typeof Permissions[keyof typeof Permissions];

// Role to permissions mapping
export const RolePermissions: Record<RoleType, PermissionType[]> = {
  [Roles.SUPERUSER]: [
    // Superuser has ALL permissions
    Permissions.DASHBOARD_READ,
    Permissions.CONTROL_TOWER_READ,
    Permissions.CONTROL_TOWER_WRITE,
    Permissions.SHIPMENTS_READ,
    Permissions.SHIPMENTS_WRITE,
    Permissions.SHIPMENTS_DELETE,
    Permissions.ROUTES_READ,
    Permissions.ROUTES_WRITE,
    Permissions.ROUTES_DELETE,
    Permissions.ALERTS_READ,
    Permissions.ALERTS_WRITE,
    Permissions.ALERTS_CONFIG,
    Permissions.FORECASTING_READ,
    Permissions.FORECASTING_WRITE,
    Permissions.SUPPLIERS_READ,
    Permissions.SUPPLIERS_WRITE,
    Permissions.ANALYTICS_READ,
    Permissions.ANALYTICS_EXPORT,
    Permissions.SITES_READ,
    Permissions.SITES_WRITE,
    Permissions.SETTINGS_READ,
    Permissions.SETTINGS_WRITE,
    Permissions.USERS_READ,
    Permissions.USERS_WRITE,
    Permissions.USERS_DELETE,
    Permissions.USERS_INVITE,
    Permissions.SYSTEM_CONFIG,
  ],

  [Roles.ADMIN]: [
    // Admin has all operational permissions plus user management
    Permissions.DASHBOARD_READ,
    Permissions.CONTROL_TOWER_READ,
    Permissions.CONTROL_TOWER_WRITE,
    Permissions.SHIPMENTS_READ,
    Permissions.SHIPMENTS_WRITE,
    Permissions.SHIPMENTS_DELETE,
    Permissions.ROUTES_READ,
    Permissions.ROUTES_WRITE,
    Permissions.ROUTES_DELETE,
    Permissions.ALERTS_READ,
    Permissions.ALERTS_WRITE,
    Permissions.ALERTS_CONFIG,
    Permissions.FORECASTING_READ,
    Permissions.FORECASTING_WRITE,
    Permissions.SUPPLIERS_READ,
    Permissions.SUPPLIERS_WRITE,
    Permissions.ANALYTICS_READ,
    Permissions.ANALYTICS_EXPORT,
    Permissions.SITES_READ,
    Permissions.SITES_WRITE,
    Permissions.SETTINGS_READ,
    Permissions.SETTINGS_WRITE,
    Permissions.USERS_READ,
    Permissions.USERS_WRITE,
    Permissions.USERS_INVITE,
  ],

  [Roles.TRANSPORT_MANAGER]: [
    // Operations focus - shipments, routes, suppliers, alerts
    Permissions.DASHBOARD_READ,
    Permissions.CONTROL_TOWER_READ,
    Permissions.SHIPMENTS_READ,
    Permissions.SHIPMENTS_WRITE,
    Permissions.ROUTES_READ,
    Permissions.ROUTES_WRITE,
    Permissions.ALERTS_READ,
    Permissions.ALERTS_WRITE,
    Permissions.FORECASTING_READ,
    Permissions.SUPPLIERS_READ,
    Permissions.SUPPLIERS_WRITE,
    Permissions.ANALYTICS_READ,
    Permissions.SITES_READ,
    Permissions.SETTINGS_READ,
  ],

  [Roles.SITE_MANAGER]: [
    // Site-specific operations
    Permissions.DASHBOARD_READ,
    Permissions.CONTROL_TOWER_READ,
    Permissions.SHIPMENTS_READ,
    Permissions.SHIPMENTS_WRITE,
    Permissions.ALERTS_READ,
    Permissions.ALERTS_WRITE,
    Permissions.SUPPLIERS_READ,
    Permissions.ANALYTICS_READ,
    Permissions.SITES_READ,
    Permissions.SETTINGS_READ,
  ],

  [Roles.PLANNER]: [
    // Route planning and forecasting focus
    Permissions.DASHBOARD_READ,
    Permissions.CONTROL_TOWER_READ,
    Permissions.SHIPMENTS_READ,
    Permissions.SHIPMENTS_WRITE,
    Permissions.ROUTES_READ,
    Permissions.ROUTES_WRITE,
    Permissions.ALERTS_READ,
    Permissions.FORECASTING_READ,
    Permissions.FORECASTING_WRITE,
    Permissions.SITES_READ,
    Permissions.SETTINGS_READ,
  ],

  [Roles.ANALYST]: [
    // Analytics and reporting focus
    Permissions.DASHBOARD_READ,
    Permissions.CONTROL_TOWER_READ,
    Permissions.SHIPMENTS_READ,
    Permissions.ROUTES_READ,
    Permissions.ALERTS_READ,
    Permissions.FORECASTING_READ,
    Permissions.SUPPLIERS_READ,
    Permissions.ANALYTICS_READ,
    Permissions.ANALYTICS_EXPORT,
    Permissions.SITES_READ,
    Permissions.SETTINGS_READ,
  ],

  [Roles.VIEWER]: [
    // Read-only access to core features
    Permissions.DASHBOARD_READ,
    Permissions.CONTROL_TOWER_READ,
    Permissions.SHIPMENTS_READ,
    Permissions.ALERTS_READ,
    Permissions.SITES_READ,
    Permissions.SETTINGS_READ,
  ],
};

// Role metadata for UI display
export interface RoleInfo {
  id: RoleType;
  name: string;
  description: string;
  level: number; // Higher number = more privileges
  color: string;
}

export const RoleInfoMap: Record<RoleType, RoleInfo> = {
  [Roles.SUPERUSER]: {
    id: Roles.SUPERUSER,
    name: 'Super User',
    description: 'Full system access including system configuration',
    level: 100,
    color: 'purple',
  },
  [Roles.ADMIN]: {
    id: Roles.ADMIN,
    name: 'Administrator',
    description: 'Full operational access with user management',
    level: 90,
    color: 'red',
  },
  [Roles.TRANSPORT_MANAGER]: {
    id: Roles.TRANSPORT_MANAGER,
    name: 'Transport Manager',
    description: 'Manage shipments, routes, and suppliers',
    level: 70,
    color: 'blue',
  },
  [Roles.SITE_MANAGER]: {
    id: Roles.SITE_MANAGER,
    name: 'Site Manager',
    description: 'Manage site-specific operations',
    level: 60,
    color: 'green',
  },
  [Roles.PLANNER]: {
    id: Roles.PLANNER,
    name: 'Planner',
    description: 'Route planning and demand forecasting',
    level: 50,
    color: 'teal',
  },
  [Roles.ANALYST]: {
    id: Roles.ANALYST,
    name: 'Analyst',
    description: 'Analytics and reporting access',
    level: 40,
    color: 'indigo',
  },
  [Roles.VIEWER]: {
    id: Roles.VIEWER,
    name: 'Viewer',
    description: 'Read-only access to dashboards',
    level: 10,
    color: 'gray',
  },
};

// Navigation permission mapping
export const NavPermissionMap: Record<string, PermissionType> = {
  '/dashboard': Permissions.DASHBOARD_READ,
  '/control-tower': Permissions.CONTROL_TOWER_READ,
  '/shipments': Permissions.SHIPMENTS_READ,
  '/routes': Permissions.ROUTES_READ,
  '/alerts': Permissions.ALERTS_READ,
  '/forecasting': Permissions.FORECASTING_READ,
  '/suppliers': Permissions.SUPPLIERS_READ,
  '/analytics': Permissions.ANALYTICS_READ,
  '/sites': Permissions.SITES_READ,
  '/settings': Permissions.SETTINGS_READ,
  '/admin/users': Permissions.USERS_READ,
};

// Helper functions
export function hasPermission(userRole: RoleType, permission: PermissionType): boolean {
  const permissions = RolePermissions[userRole];
  if (!permissions) return false;
  return permissions.includes(permission);
}

export function hasAnyPermission(userRole: RoleType, permissions: PermissionType[]): boolean {
  return permissions.some(permission => hasPermission(userRole, permission));
}

export function hasAllPermissions(userRole: RoleType, permissions: PermissionType[]): boolean {
  return permissions.every(permission => hasPermission(userRole, permission));
}

export function canAccessPath(userRole: RoleType, path: string): boolean {
  const permission = NavPermissionMap[path];
  if (!permission) return true; // No permission required
  return hasPermission(userRole, permission);
}

export function getRoleInfo(role: RoleType): RoleInfo {
  return RoleInfoMap[role] || RoleInfoMap[Roles.VIEWER];
}

export function isAdminRole(role: RoleType): boolean {
  return role === Roles.SUPERUSER || role === Roles.ADMIN;
}

export function canManageUsers(role: RoleType): boolean {
  return hasPermission(role, Permissions.USERS_READ);
}

// Get all roles a user can assign (must be lower level than their own)
export function getAssignableRoles(userRole: RoleType): RoleType[] {
  const userLevel = RoleInfoMap[userRole]?.level || 0;
  return Object.values(Roles).filter(role => {
    const roleLevel = RoleInfoMap[role]?.level || 0;
    return roleLevel < userLevel;
  });
}
